/**
 * Citation Locator Helper
 * Determines appropriate locator types for different citation sources and formats user input
 * Based on APA 7th Edition guidelines for in-text citation locators
 */

export const LOCATOR_TYPES = {
	PAGE: 'page',
	TIMESTAMP: 'timestamp',
	PARAGRAPH_OR_SECTION: 'paragraph_or_section',
	SLIDE: 'slide',
	FIGURE: 'figure',
	NONE: 'none'
};

/**
 * Get locator type based on citation's mainCategory and subcategory
 * @param {string} mainCategory - The main category from backend
 * @param {string} subcategory - The subcategory from backend
 * @returns {string} - Locator type from LOCATOR_TYPES
 */
export function getLocatorTypeFromCitation(mainCategory, subcategory) {
	// Books & Chapters - Use PAGE
	if (mainCategory === 'Books & Chapters' || mainCategory === 'Book & Chapter') {
		return LOCATOR_TYPES.PAGE;
	}

	// Periodicals - Use PAGE for print, PARAGRAPH for online
	if (mainCategory === 'Periodicals') {
		// Default to PAGE (most common for academic articles)
		return LOCATOR_TYPES.PAGE;
	}

	// Web Content - Use PARAGRAPH or SECTION
	if (mainCategory === 'Web Content') {
		if (subcategory === 'Social Media Post') {
			return LOCATOR_TYPES.PARAGRAPH_OR_SECTION; // For threads: para. 2
		}
		return LOCATOR_TYPES.PARAGRAPH_OR_SECTION; // Webpage, Blog - para. 4 or in "Section"
	}

	// Reports & Government Docs - Use PAGE or PARAGRAPH
	if (mainCategory === 'Reports & Government Docs') {
		// Check descriptor for Press Release (uses paragraphs)
		if (subcategory === 'Report') {
			// Most reports are PDFs with page numbers
			return LOCATOR_TYPES.PAGE;
		}
		return LOCATOR_TYPES.PAGE;
	}

	// Data & Software - No locator (cited as a whole)
	if (mainCategory === 'Data & Software') {
		return LOCATOR_TYPES.NONE;
	}

	// Multimedia - Use TIMESTAMP
	if (mainCategory === 'Multimedia') {
		return LOCATOR_TYPES.TIMESTAMP;
	}

	// Conferences & Presentations - Use SLIDE or TIMESTAMP
	if (mainCategory === 'Conferences & Presentations') {
		return LOCATOR_TYPES.SLIDE; // Default to slide, can also support timestamp
	}

	// Figures, Infographics & Charts - Use FIGURE
	if (mainCategory === 'Figures, Infographics & Charts') {
		return LOCATOR_TYPES.FIGURE;
	}

	// Social & Personal Communication - Usually none, except social media threads
	if (mainCategory === 'Social & Personal Communication') {
		if (subcategory === 'Social Media Post') {
			return LOCATOR_TYPES.PARAGRAPH_OR_SECTION; // For post # in thread
		}
		return LOCATOR_TYPES.NONE;
	}

	// Default to PAGE for academic sources
	return LOCATOR_TYPES.PAGE;
}

/**
 * Format user input based on locator type (smart auto-formatting!)
 * Implements APA 7th edition formatting rules
 * @param {string} locatorType - Type from LOCATOR_TYPES
 * @param {string} userInput - Raw user input (e.g., "23", "4:15", "Introduction")
 * @returns {string} - Formatted locator (e.g., "p. 23", "04:15", "in \"Introduction\"")
 */
export function formatLocatorValue(locatorType, userInput) {
	if (!userInput || !userInput.trim()) return '';

	const value = userInput.trim().replace(/\.$/, '');

	switch (locatorType) {
		case LOCATOR_TYPES.PAGE: {
			// Check if user explicitly provided "para." or "paras."
			if (/^paras?\.?/i.test(value)) {
				return value; // Return as-is (e.g. "para. A6")
			}

			// Replace hyphens with en-dashes for ranges
			const formattedValue = value.replace(/-/g, '–');
			// Detect if it's a range (contains dash or en-dash) or list (comma)
			const isRange = formattedValue.includes('–') || formattedValue.includes(',');

			// Single page: p. 23
			// Page range: pp. 23–25
			return isRange ? `pp. ${formattedValue}` : `p. ${formattedValue}`;
		}

		case LOCATOR_TYPES.PARAGRAPH_OR_SECTION: {
			// Check if input is a number, range, or list (allow digits, spaces, commas, hyphens, en-dashes)
			// But NOT letters (unless part of a section title, which usually has words)
			const isNumberOrRange = /^[\d\s,–-]+$/.test(value);

			if (isNumberOrRange) {
				// It's a paragraph number/range
				const formattedValue = value.replace(/-/g, '–');
				const isRange = formattedValue.includes('–') || formattedValue.includes(',');
				// Single paragraph: para. 4
				// Multiple paragraphs: paras. 4–6
				return isRange ? `paras. ${formattedValue}` : `para. ${formattedValue}`;
			} else {
				// It's a section name (e.g., "Introduction", "Discussion")
				// Format: in "Section Name"
				return `in "${value}"`;
			}
		}

		case LOCATOR_TYPES.TIMESTAMP: {
			// Pad with zeros for consistent formatting
			// Input: 4:15 → Output: 04:15
			// Input: 1:22:05 → Output: 01:22:05
			const parts = value.split(':');

			if (parts.length === 2) {
				// MM:SS format
				const [min, sec] = parts.map((p) => p.padStart(2, '0'));
				return `${min}:${sec}`;
			} else if (parts.length === 3) {
				// HH:MM:SS format
				const [hr, min, sec] = parts.map((p) => p.padStart(2, '0'));
				return `${hr}:${min}:${sec}`;
			}

			// Return as-is if format is unclear
			return value;
		}

		case LOCATOR_TYPES.SLIDE: {
			const formattedValue = value.replace(/-/g, '–');
			const isRange = formattedValue.includes('–') || formattedValue.includes(',');

			// Single slide: Slide 7
			// Multiple slides: Slides 7–9
			return isRange ? `Slides ${formattedValue}` : `Slide ${formattedValue}`;
		}

		case LOCATOR_TYPES.FIGURE: {
			// If user just enters "2", prepend "Figure"
			if (/^\d+$/.test(value)) {
				return `Figure ${value}`;
			}
			// User already typed "Figure 2", return as-is
			return value;
		}

		default:
			return value;
	}
}

/**
 * Get label for the locator input field
 * @param {string} locatorType - Type from LOCATOR_TYPES
 * @returns {string} - Label text for UI
 */
export function getLocatorLabel(locatorType) {
	switch (locatorType) {
		case LOCATOR_TYPES.PAGE:
			return 'Page number(s)';
		case LOCATOR_TYPES.TIMESTAMP:
			return 'Timestamp';
		case LOCATOR_TYPES.PARAGRAPH_OR_SECTION:
			return 'Paragraph number or Section title';
		case LOCATOR_TYPES.SLIDE:
			return 'Slide number(s)';
		case LOCATOR_TYPES.FIGURE:
			return 'Figure reference';
		default:
			return 'Locator';
	}
}

/**
 * Get placeholder text for the locator input
 * @param {string} locatorType - Type from LOCATOR_TYPES
 * @returns {string} - Placeholder text
 */
export function getLocatorPlaceholder(locatorType) {
	switch (locatorType) {
		case LOCATOR_TYPES.PAGE:
			return '23 or 45-46';
		case LOCATOR_TYPES.TIMESTAMP:
			return '04:15 or 01:22:05';
		case LOCATOR_TYPES.PARAGRAPH_OR_SECTION:
			return '4 or "Introduction"';
		case LOCATOR_TYPES.SLIDE:
			return '7 or 10-12';
		case LOCATOR_TYPES.FIGURE:
			return 'Figure 2';
		default:
			return '';
	}
}

/**
 * Get tooltip/help text for the locator field
 * @param {string} locatorType - Type from LOCATOR_TYPES
 * @returns {string} - Help text
 */
export function getLocatorHelpText(locatorType) {
	switch (locatorType) {
		case LOCATOR_TYPES.PAGE:
			return "Enter the location for the cited text.";
		case LOCATOR_TYPES.TIMESTAMP:
			return 'Enter the time if citing a specific moment in an audio or video source (e.g., 01:02:15).';
		case LOCATOR_TYPES.PARAGRAPH_OR_SECTION:
			return 'Enter the paragraph number if the source has no page numbers (e.g., para. 4).Enter the heading or section title if no page or paragraph number is available (e.g., “Results” section). Use this for long or detailed webpages.';
		case LOCATOR_TYPES.SLIDE:
			return 'Enter the slide number(s) when citing a presentation or slideshow (e.g., Slide 4 or Slides 4–5).';
		case LOCATOR_TYPES.FIGURE:
			return 'Enter figure reference as it appears in the source (e.g., Figure 2)';
		default:
			return 'Enter the specific location where the quote appears';
	}
}

/**
 * Validate locator input based on type
 * @param {string} locatorType - Type from LOCATOR_TYPES
 * @param {string} userInput - User's input
 * @returns {object} - { isValid: boolean, error: string }
 */
export function validateLocatorInput(locatorType, userInput) {
	if (!userInput || !userInput.trim()) {
		return { isValid: true, error: '' }; // Empty is okay (locator is optional)
	}

	const value = userInput.trim();

	// Check for redundant labels (e.g. "page 5", "p. 5")
	const invalidLabelPattern = /^(pages?|p\.?|pp\.?|paras?|paragraphs?|chapters?|chaps?|ch\.?|vols?|slides?|figures?|figs?)\b[.\s]*/i;

	if (invalidLabelPattern.test(value)) {
		// Skip check for FIGURE type as users might type "Figure 2" explicitly
		if (locatorType === LOCATOR_TYPES.FIGURE) {
			return { isValid: true, error: '' };
		}

		// Allow "para." or "paras." for PAGE type (Newspapers/Magazines can be online)
		if (locatorType === LOCATOR_TYPES.PAGE && /^paras?\.?/i.test(value)) {
			// Do nothing here, let it pass to specific validation logic if needed
		} else {
			// Provide specific guidance based on type
			switch (locatorType) {
				case LOCATOR_TYPES.PAGE:
					return { isValid: false, error: "Please enter only the page number(s) (e.g., 7 or 15-16). We automatically add 'p.' or 'pp.'." };
				case LOCATOR_TYPES.SLIDE:
					return { isValid: false, error: "Please enter only the slide number(s) (e.g., 4). We automatically add 'Slide'." };
				case LOCATOR_TYPES.PARAGRAPH_OR_SECTION:
					// If they typed "para" or "paragraph"
					if (/^(paras?|paragraphs?)\b/i.test(value)) {
						return { isValid: false, error: "Please enter only the paragraph number (e.g., 4). We automatically add 'para.'." };
					}
					// If they typed "chapter", "page", etc. in a Paragraph/Section field
					return { isValid: false, error: "Please enter the paragraph number (e.g., 4) or section title (e.g., Results). Avoid prefixes like 'page' or 'chapter'." };
				default:
					return { isValid: false, error: "Please enter only the value. Do not include labels like 'page' or 'para'." };
			}
		}
	}

	switch (locatorType) {
		case LOCATOR_TYPES.PAGE:
			// Should be number, range, or list - matches tooltip "number (p. 7) or range (pp. 15-16)" (implied numbers only)
			if (!/^[\d\s,–-]+$/.test(value)) {
				return {
					isValid: false,
					error: 'Please enter a valid page number, range (e.g., 15-16), or list (e.g., 10, 15).'
				};
			}
			return { isValid: true, error: '' };

		case LOCATOR_TYPES.SLIDE:
			// Should be number, range, or list - matches tooltip "slide number(s)... (e.g. 4 or 4-5)"
			if (!/^[\d\s,–-]+$/.test(value)) {
				return {
					isValid: false,
					error: 'Please enter a valid slide number or range (e.g., 4 or 4-5).'
				};
			}
			return { isValid: true, error: '' };

		case LOCATOR_TYPES.TIMESTAMP:
			// Should be MM:SS or HH:MM:SS format - matches tooltip "01:02:15"
			if (!/^\d{1,2}:\d{1,2}(:\d{1,2})?$/.test(value)) {
				return {
					isValid: false,
					error: 'Please enter timestamp in MM:SS or HH:MM:SS format (e.g., 01:02:15).'
				};
			}
			return { isValid: true, error: '' };

		case LOCATOR_TYPES.PARAGRAPH_OR_SECTION:
			// Can be number, range, or text
			// No specific regex validation enforced beyond the Label check above.
			return { isValid: true, error: '' };

		case LOCATOR_TYPES.FIGURE:
			// Any text is valid
			return { isValid: true, error: '' };

		default:
			return { isValid: true, error: '' };
	}
}
